import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { initGA, trackRouterPageView } from './utils/analytics';
import { AnimatePresence, motion } from 'framer-motion';
import Navigation from './components/Navbar';
import Home from './components/Home';
import Loader from './components/Loader';
import Libro from './components/Libro';
import Personajes from './components/Personajes';
import Section3 from './components/Section3';
import Capitulos from './components/Capitulos';
import Eventos from './components/Eventos';
import Capitulo1 from './components/Captitulo1';
import Capitulo2 from './components/Captitulo2';
import Capitulo3 from './components/Captitulo3';
import Capitulo4 from './components/Captitulo4';
import Capitulo5 from './components/Captitulo5';
import Capitulo6 from './components/Captitulo6';
import Capitulo7 from './components/Captitulo7';
import Capitulo8 from './components/Captitulo8';
import Capitulo9 from './components/Captitulo9';
import Capitulo10 from './components/Captitulo10';
import Capitulo11 from './components/Captitulo11';
import Capitulo12 from './components/Captitulo12';
import Capitulo13 from './components/Captitulo13';
import Capitulo14 from './components/Captitulo14';
import ProfileForm from './components/ProfileForm';
import CharacterForm from './components/CharacterForm';
import QRRedirect from './components/QRRedirect';
import Auth from './components/Auth';
import Contact from './components/Contact';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import AdminPanel from './components/AdminPanel';
import NotFound from './components/NotFound';
import useUrlTracking from './hooks/useUrlTracking';
import { ActiveSectionProvider } from './contexts/ActiveSectionContext';
import { SectionProvider } from './contexts/SectionContext';
import ScrollToTop from './components/ScrollToTop';

function App() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    initGA();
    trackRouterPageView(location); // Initial page load
  }, [location]);

  useEffect(() => {
    trackRouterPageView(location); // Subsequent route changes
  }, [location]);

  useUrlTracking();

  useEffect(() => {
    const checkAppLoaded = async () => {
      try {
        // Track critical resources that need to be loaded
        const criticalResources = [
          // Add any critical API calls, images, or data that must load
          // Example: fetchInitialData(), waitForImages(), etc.
        ];

        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setLoadingProgress(prev => {
            const newProgress = prev + Math.random() * 10;
            return newProgress >= 90 ? 90 : newProgress;
          });
        }, 200);

        // Wait for all critical resources to load
        await Promise.all(criticalResources);

        // If you have any specific components that need to be ready, add them here
        // For example, if you need to wait for user authentication or initial data:
        // await checkAuthStatus();
        // await loadInitialAppData();

        // Simulate minimum loading time for better UX (optional)
        const minLoadTime = Promise.all([
          new Promise(resolve => setTimeout(resolve, 800)), // Minimum 800ms loading
          ...criticalResources
        ]);

        await minLoadTime;

        clearInterval(progressInterval);
        setLoadingProgress(100);
        
        // Small delay to show 100% progress
        setTimeout(() => setIsLoading(false), 300);
        
      } catch (error) {
        console.error('Error during app loading:', error);
        // Even if there's an error, we should show the app
        setIsLoading(false);
      }
    };

    checkAppLoaded();
  }, []);

  // Alternative approach: Check when DOM is ready and critical components are mounted
  useEffect(() => {
    const handleAppReady = () => {
      // Check if critical elements are in the DOM
      const isDOMReady = document.readyState === 'complete';
      
      if (isDOMReady) {
        // You can add additional checks here for specific components or data
        setIsLoading(false);
      }
    };

    // If DOM is already complete, set loading to false immediately
    if (document.readyState === 'complete') {
      setIsLoading(false);
    } else {
      window.addEventListener('load', handleAppReady);
      
      // Fallback: if everything else fails, stop loading after 3 seconds
      const fallbackTimer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);

      return () => {
        window.removeEventListener('load', handleAppReady);
        clearTimeout(fallbackTimer);
      };
    }
  }, []);

  if (isLoading) {
    return <Loader fullPage={true} variant="primary" progress={loadingProgress} />;
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      <ScrollToTop />
      <ActiveSectionProvider>
        <Navigation />

        <div className="flex-grow-1">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>

              {/* Home Page - Fade Animation */}
              <Route path="/" element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="motion-div"
                >
                  <Home />
                </motion.div>
              } />

              {/* Section 1 - Slide From Right */}
              <Route
                path="/el-libro"
                element={
                  <SectionProvider sectionClass="percha-bg-color">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Libro />
                    </motion.div>
                  </SectionProvider>
                }
              />

              {/* Section 2 - Slide From Bottom */}
              <Route
                path="/personajes"
                element={
                  <SectionProvider sectionClass="patzcuaro-bg-color">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Personajes />
                    </motion.div>
                  </SectionProvider>
                } />

                {/* Events */}
              <Route
                path="/eventos"
                element={
                  <SectionProvider sectionClass="events-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Eventos />
                    </motion.div>
                  </SectionProvider>
                } />

              {/* Login Page - Scale Animation */}
              <Route path="/login" element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="motion-div"
                >
                  <Auth />
                </motion.div>
              } />

              {/* Login Page - Scale Animation */}
              <Route path="/contact" element={
                <SectionProvider sectionClass="percha-bg-color">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="motion-div"
                >
                  <Contact />
                </motion.div>
                </SectionProvider>
              } />

              {/* Protected Routes */}
              <Route path="/section3" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Section3 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/7pX9qR2tK5wY8vZ3sN6cB4mH1dL" element={
                <SectionProvider sectionClass="section3-bg">
                  <motion.div
                    initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    className="motion-div"
                  >
                    <Capitulo1 />
                  </motion.div>
                </SectionProvider>
              } />

              {/* Protected Routes */}
              <Route path="/salida-a-tzintzuntzan" element={
                <SectionProvider sectionClass="section3-bg">
                  <motion.div
                    initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    className="motion-div"
                  >
                    <Capitulo1 />
                  </motion.div>
                </SectionProvider>
              } />

              {/* Protected Routes */}
              <Route path="/3bC6dF9gH2jK5mN8pQ1rS4tV7y" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo2 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/8yT3rE5wQ9uI2oP7aS1dF4gH6j" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo3 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/2sD5fG8hJ1kL4mN7pQ0rT3uV6" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo4 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/Q3mN6bV9cX1zZ4wE7rT2yU5i8" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo5 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/6pX9qR2tK5wY8vZ3sN6cB4mH1dL" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section6-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo6 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/5tR8uY2iP4oA7sD1fG6hJ9kL3" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo7 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/H4jK7mN1bV3cX6zZ9wE2rT5y" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo8 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/6gH9jK2lM5nP8qS1vW4xY7zZ3" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo9 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/3dF6gH1jK4lM7nP0qS2vW5xY8" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo10 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/7vW2xY5zZ8aB1cD4eF6gH9jK3" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo11 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/4nP7qS2vW5xY8zZ1aB3cD6eF9gH" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo12 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/8kL3mN6bV9cX2zZ5wE1rT4yU7i" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo13 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              {/* Protected Routes */}
              <Route path="/zX5cV8bN2mK9jL6hG3dF7gS1aQ4wE" element={
                <ProtectedRoute>
                  <SectionProvider sectionClass="section3-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <Capitulo14 />
                    </motion.div>
                  </SectionProvider>
                </ProtectedRoute>
              } />

              <Route path="/capitulos" element={
                <SectionProvider sectionClass="mictlan-bg-color">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="motion-div"
                  >
                    <Capitulos />
                  </motion.div>
                </SectionProvider>
              } />

              <Route
                path="/tu-perfil"
                element={
                  <ProtectedRoute>
                    <SectionProvider sectionClass="profile-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <ProfileForm />
                    </motion.div>
                    </SectionProvider>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/add-character"
                element={
                  <ProtectedRoute>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <CharacterForm />
                    </motion.div>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/adminpanel"
                element={
                  <ProtectedRoute>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <AdminPanel />
                    </motion.div>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/qr/:code"
                element={
                  <QRRedirect />
                }
              />

              <Route 
                path="*" 
                element={
                  <SectionProvider sectionClass="notFound-bg">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="motion-div"
                    >
                      <NotFound />
                    </motion.div>
                  </SectionProvider>
                } 
              />
            </Routes>

          </AnimatePresence>
        </div>

        <Footer />
      </ActiveSectionProvider>
    </div>
  );
}

export default App;